import {
  AttachFileOutlined,
  ChecklistRtlOutlined,
  CodeOffOutlined,
  CodeOutlined,
  FeedOutlined,
  NotificationsActiveOutlined,
  OpenInNew,
} from "@mui/icons-material";
import { Box, Card, IconButton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isEmpty, size } from "lodash";
import { useMemo, useState } from "react";
import FlxGrid from "../../_src_shared/components/layout/FlxGrid";
import { formatBorrowersArray } from "../../_src_shared/utils/arrays/formatBorrowerArray";
import { formatSubjectPropertiesArray } from "../../_src_shared/utils/arrays/formatSubjectPropertiesArray";

import { formatDateShort } from "../../utils/dates/formatDateShort";
import AddressMap from "../common/AddressMap";
import JsonPreview from "../common/JsonPreview";
import Flx from "../layout/Flx";
import SectionDivider from "../layout/SectionDivider";
import EnhancedTabSwitcher from "../navigation/EnhancedTabSwitcher";
import TabPanel from "../navigation/TabPanel";
import LoanRequiredDocsRenderer from "../renderers/LoanRequiredDocsRenderer";

import { formatLoanName } from "../../_src_shared/utils/formatters/formatLoanName";
import Txt from "../typography/Txt";
import InfoGrid from "../ui/data/InfoGrid";
import InfoGridItem from "../ui/data/InfoGridItem";
import FinancingRequestConditionsTab from "./components/FinancingRequestConditionsTab";
import FinancingRequestFilesTab from "./components/FinancingRequestFilesTab";
import FinancingRequestHeader from "./components/FinancingRequestHeader";
import FinancingRequestTermsSheetTab from "./components/FinancingRequestTermsSheetTab";
import { useSelector } from "react-redux";

const TABS = {
  LOAN_DETAILS: "Loan Details",
  CONDITIONS: "Active Conditions",
  UPLOADED_FILES: "Uploaded Documents",
  TERMS_SHEET: "Term Sheet",
  CHECKLIST: "Document Checklist",
};

const FinancingRequestItem = ({ data, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState(
    defaultOpen ? TABS.CONDITIONS : TABS.LOAN_DETAILS,
  );

  const profile = useSelector((state) => state?.profile);
  const dealId = data?._id;

  // Pending Terms
  const loanTermsSheets = useMemo(() => {
    const termSheets = profile?.termSheets || [];
    const matchingTermSheets = termSheets.filter((ts) => ts.dealId === dealId);
    if (isEmpty(matchingTermSheets)) {
      return [];
    }
    return matchingTermSheets;
  }, [profile?.termSheets, dealId]);

  // Pending Terms
  const loanPendingTermsSheets = useMemo(() => {
    const profilePendingTermSheets = profile?.pendingTermSheets || [];
    const matchingTermSheets = profilePendingTermSheets.filter(
      (ts) => ts.dealId === dealId,
    );
    if (isEmpty(matchingTermSheets)) {
      return [];
    }
    return matchingTermSheets;
  }, [profile?.pendingTermSheets, dealId]);

  // Accepted Terms
  const loanAcceptedTermsSheets = useMemo(() => {
    const profileAcceptedTermSheets = profile?.acceptedTermSheets || [];
    const matchingTermSheets = profileAcceptedTermSheets.filter(
      (ts) => ts?.dealId === dealId,
    );

    if (isEmpty(matchingTermSheets)) {
      return [];
    }
    return matchingTermSheets;
  }, [profile?.acceptedTermSheets, dealId]);

  const TABS_LIST = [
    {
      value: TABS.LOAN_DETAILS,
      label: TABS.LOAN_DETAILS,
      mobileLabel: "Details",
      icon: <FeedOutlined />,
    },

    {
      value: TABS.CONDITIONS,
      label: TABS.CONDITIONS,
      mobileLabel: "Conditions",
      icon: <NotificationsActiveOutlined />,
      badge: {
        show: !isEmpty(data?.underwritingRequirements) ? true : false,
        variant: "standard",
        content: size(
          data?.underwritingRequirements?.filter(
            (doc) => doc?.status !== "Accepted",
          ),
        ),
        color: "error",
      },
    },
    {
      value: TABS.UPLOADED_FILES,
      label: `Loan Documents (${size(data?.loanDocuments)})`,
      mobileLabel: "Documents",
      icon: <AttachFileOutlined />,
    },

    {
      value: TABS.TERMS_SHEET,
      label: `Terms Sheet`,
      // label: `Terms Sheet (${size(data?.loanDocuments?.filter((doc) => doc.docType === "Term Sheet")) === 1 ? "Pending Review" : 0})`,
      mobileLabel: "Terms",
      icon: <AttachFileOutlined />,
      badge: {
        show:
          !isEmpty(loanAcceptedTermsSheets) || isEmpty(loanTermsSheets)
            ? false
            : true,

        // show:
        //   !isEmpty(loanAcceptedTermsSheets) ||
        //   (!isEmpty(loanPendingTermsSheets) &&
        //     !isEmpty(loanAcceptedTermsSheets))
        //     ? false
        //     : true,
        variant: "dot",
        color: "error",
      },
    },
  ];

  const {
    loanPurpose,
    loanProductType,
    loanStatus,
    loanNumber,
    expectedClosingDate,
    createDateTime,
    borrowers = [],
    subjectProperties = [],
  } = data;

  const displayedItems = useMemo(() => {
    return [
      { label: "Status", value: loanStatus },
      { label: "Product Type", value: loanProductType },
      { label: "Loan Purpose", value: loanPurpose },
      { label: "Created Date", value: formatDateShort(createDateTime) },
      { label: "Expected Close", value: formatDateShort(expectedClosingDate) },
      { label: "Loan Number", value: loanNumber },
    ];
  }, [
    loanStatus,
    expectedClosingDate,
    loanNumber,
    createDateTime,
    loanProductType,
    loanPurpose,
  ]);

  const MODAL_TYPE_NAME = `upload-modal-${data?._id}`;
  const [formSpy, setFormSpy] = useState(false);
  return (
    <Card
      sx={{
        p: 2.5,

        display: "flex",
        flexDirection: "column",
        borderRadius: "10px",
        boxShadow: open
          ? "0 15px 40px rgba(0,0,0,0.25)"
          : "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <FinancingRequestHeader
        loanName={formatLoanName(data)}
        loanProductType={loanProductType}
        loanPurpose={loanPurpose || "N/A"}
        loanStatus={loanStatus}
        open={open}
        loan={data}
        modalTypeName={MODAL_TYPE_NAME}
        loanDocuments={data?.loanDocuments}
        setOpen={setOpen}
      />
      {open && (
        <Flx g={2} column sx={{ mt: 2.5, position: "relative" }}>
          {process.env.NODE_ENV === "development" && (
            <IconButton
              size="small"
              sx={{ position: "absolute", top: 0, right: 0, opacity: 0.3 }}
              onClick={() => setFormSpy(!formSpy)}
            >
              {formSpy ? <CodeOffOutlined /> : <CodeOutlined />}
            </IconButton>
          )}

          <EnhancedTabSwitcher
            value={tab}
            wrap
            onChange={setTab}
            tabs={TABS_LIST}
            orientation={"horizontal"}
            // orientation={isMobile ? "horizontal" : "vertical"}
          />

          <Box sx={{ position: "relative" }} className="tab-content-container">
            <TabPanel tabValue={tab} value={TABS.LOAN_DETAILS}>
              <Box
                className="loan-details-container"
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 0,
                }}
              >
                <InfoGrid items={displayedItems} />

                <SectionDivider />

                <FlxGrid
                  width="120px"
                  grow={1}
                  sx={{
                    gap: "16px 22px",
                  }}
                >
                  <InfoGridItem
                    label="Borrowers"
                    value={formatBorrowersArray(borrowers)}
                    fullWidth
                  />
                  <InfoGridItem
                    label="Property"
                    value={formatSubjectPropertiesArray(subjectProperties)}
                    fullWidth
                  />
                </FlxGrid>
                <Box
                  sx={{
                    position: "relative",
                    border: `1px solid ${grey[100]}`,
                    overflow: "hidden",
                    borderRadius: "8px",
                    backgroundColor: grey[200],
                    mt: 4,
                    // mb: 4,

                    height: "240px",
                  }}
                >
                  <AddressMap
                    height="100%"
                    sx={{ position: "relative", zIndex: 11 }}
                    address={subjectProperties[0]?.address}
                  />
                  <Txt
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      zIndex: 1,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    Loading map...
                  </Txt>
                  <OpenLocationButton
                    address={subjectProperties[0]?.address?.fullAddress}
                  />
                </Box>
              </Box>
            </TabPanel>
            <TabPanel tabValue={tab} value={TABS.CONDITIONS}>
              <FinancingRequestConditionsTab
                loanId={data?._id}
                conditions={data?.underwritingRequirements}
              />
            </TabPanel>
            <TabPanel tabValue={tab} value={TABS.CHECKLIST}>
              <LoanRequiredDocsRenderer
                dealId={data?._id}
                loan={data}
                width="540px"
                files={data?.loanDocuments}
              />
            </TabPanel>
            <TabPanel tabValue={tab} value={TABS.TERMS_SHEET}>
              <FinancingRequestTermsSheetTab
                dealId={data?._id}
                pendingTermSheets={loanPendingTermsSheets}
              />
            </TabPanel>
            <TabPanel tabValue={tab} value={TABS.UPLOADED_FILES}>
              <FinancingRequestFilesTab
                dealId={data?._id}
                loanDocuments={data?.loanDocuments}
              />
            </TabPanel>
          </Box>
        </Flx>
      )}
      {formSpy && <JsonPreview values={data} />}
    </Card>
  );
};

const OpenLocationButton = ({ lat, lng, address, sx = {} }) => {
  const hasCoords = lat != null && lng != null;
  const hasAddress = Boolean(address);

  const handleClick = () => {
    let url = "";

    if (hasCoords) {
      url = `https://www.google.com/maps?q=${lat},${lng}`;
    } else if (hasAddress) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address,
      )}`;
    }

    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Tooltip title="Open in Google Maps">
      <IconButton
        onClick={handleClick}
        disabled={!hasCoords && !hasAddress}
        sx={{
          position: "absolute",
          top: 14,
          right: 14,
          background: "#ffffff",
          border: `1px solid ${grey[300]}`,
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          zIndex: 11,
          "&:hover": {
            background: "#ffffff",
          },
          ...sx,
        }}
      >
        <OpenInNew className="thin" />
      </IconButton>
    </Tooltip>
  );
};

export default FinancingRequestItem;
